/**
 * Theme management hook for the Phishiness frontend
 * Syncs Zustand theme state with document class for TailwindCSS dark mode
 */

import { useEffect } from 'react';
import { useUIStore } from '@/store/uiStore';

/**
 * Hook that pins the app to its single dark theme.
 *
 * The product is dark-only (terminal-on-OLED — no light mode). The Zustand
 * theme store + setTheme/toggle are kept in place (reversible), but this hook
 * unconditionally enforces the `dark` document class regardless of stored
 * state, so the app can never render light. Should be called once at the app
 * root level.
 */
export function useTheme() {
  const theme = useUIStore((state) => state.theme);
  const setTheme = useUIStore((state) => state.setTheme);

  // Force the dark class on every render; the app has no light mode.
  useEffect(() => {
    window.document.documentElement.classList.add('dark');
  }, []);

  // No-op toggle retained for API compatibility with existing callers.
  const toggleTheme = () => {
    setTheme('dark');
  };

  return { theme, setTheme, toggleTheme };
}

export default useTheme;
